import { api, ApiError, explain } from './api';
import type { Experiment, Goal } from './api';
import { alertBox, clear, field, h, statusBox } from './dom';

const KINDS = [
  {
    value: 'click',
    label: 'A visitor clicks something',
    ask: 'CSS selector of the thing to click',
    hint: '.buy-button',
  },
  {
    value: 'pageview',
    label: 'A visitor reaches a page',
    ask: 'Page address contains',
    hint: '/thank-you',
  },
  {
    value: 'custom',
    label: "My site reports it with xp.track('...')",
    ask: 'Event name',
    hint: 'purchase',
  },
  {
    value: 'datalayer',
    label: 'Google Tag Manager sends a dataLayer event',
    ask: 'Event name',
    hint: 'purchase',
  },
] as const;

type Kind = (typeof KINDS)[number]['value'];

/** The goal as the API stores it, from what the form holds. */
export function buildGoal(
  kind: Kind,
  name: string,
  value: string,
  revenue: boolean,
  id: string,
): Goal {
  const common = { id, name, ...(revenue ? { revenue: true } : {}) };
  if (kind === 'click') return { ...common, type: 'click', selector: value };
  if (kind === 'pageview')
    return { ...common, type: 'pageview', rule: { type: 'contains', value } };
  return { ...common, type: kind, event: value };
}

/** What a goal means, in words. */
export function describeGoal(g: Goal): string {
  const what =
    g.type === 'click'
      ? `a visitor clicks ${g.selector}`
      : g.type === 'pageview'
        ? `a visitor reaches a page whose address ${g.rule.type === 'exact' ? 'is' : 'contains'} ${g.rule.value}`
        : g.type === 'custom'
          ? `the site reports "${g.event}"`
          : `Tag Manager sends "${g.event}"`;
  return g.revenue ? `${what}; the value sent counts as revenue` : what;
}

const newId = () => `g_${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;

/**
 * Goals: what counts as success. Without one an experiment has no results, so adding one is part of
 * setting it up, not an advanced setting.
 */
export function goalsPanel(
  exp: Experiment,
  signal: AbortSignal,
  afterSave: () => Promise<void>,
): HTMLElement {
  const box = h('section', { 'aria-label': 'Goals' });
  const list = h('ul', { class: 'cards' });
  const msg = h('div', { 'aria-live': 'polite' });

  const save = async (goals: Goal[]) => {
    clear(msg);
    try {
      const r = await api<{ experiment: Experiment }>('PATCH', `/experiments/${exp.id}`, {
        goals,
        expectedVersion: exp.version,
      });
      exp.goals = r.experiment.goals;
      exp.version = r.experiment.version;
      draw();
      try {
        await afterSave();
      } catch {
        msg.append(
          alertBox(
            'The goal was saved, but publishing it failed, so visitors are not being measured against it yet. Open the site page to see why.',
          ),
        );
        return true;
      }
      msg.append(statusBox('Saved.'));
      return true;
    } catch (e) {
      if (signal.aborted) return false;
      const details =
        e instanceof ApiError && e.details.length ? e.details.map((d) => d.message).join('; ') : '';
      msg.append(
        alertBox(
          e instanceof ApiError && e.code === 'version_conflict'
            ? 'Someone else changed this experiment. Reload the page and try again.'
            : details || explain(e, 'Could not save the goal.'),
        ),
      );
      return false;
    }
  };

  const draw = () => {
    clear(list);
    if (!exp.goals.length)
      list.append(
        h(
          'li',
          {},
          h('span', { class: 'muted' }, 'No goals yet. Without a goal there are no results.'),
        ),
      );
    for (const g of exp.goals) {
      const remove = h(
        'button',
        { type: 'button', class: 'danger', 'aria-label': `Remove goal ${g.name ?? g.id}` },
        'Remove',
      );
      remove.addEventListener('click', async () => {
        remove.disabled = true;
        if (!(await save(exp.goals.filter((x) => x.id !== g.id)))) remove.disabled = false;
      });
      list.append(
        h(
          'li',
          {},
          h('strong', {}, g.name ?? g.id),
          ' ',
          h('span', { class: 'muted' }, describeGoal(g)),
          ' ',
          remove,
        ),
      );
    }
  };
  draw();

  const name = h('input', {
    type: 'text',
    maxlength: '80',
    autocomplete: 'off',
  }) as HTMLInputElement;
  const kind = h(
    'select',
    {},
    ...KINDS.map((k) => h('option', { value: k.value }, k.label)),
  ) as HTMLSelectElement;
  const value = h('input', {
    type: 'text',
    autocomplete: 'off',
    maxlength: '200',
  }) as HTMLInputElement;
  const valueLabel = h('span', { class: 'lbl' }, KINDS[0].ask);
  const revenue = h('input', { type: 'checkbox' }) as HTMLInputElement;
  const sync = () => {
    const k = KINDS.find((x) => x.value === kind.value)!;
    valueLabel.textContent = k.ask;
    value.placeholder = k.hint;
  };
  kind.addEventListener('change', sync);
  sync();
  const add = h('button', { type: 'button' }, 'Add goal');
  add.addEventListener('click', async () => {
    clear(msg);
    if (!name.value.trim() || !value.value.trim()) {
      msg.append(alertBox('Give the goal a name and say what counts.'));
      return;
    }
    add.disabled = true;
    const goal = buildGoal(
      kind.value as Kind,
      name.value.trim(),
      value.value.trim(),
      revenue.checked,
      newId(),
    );
    if (await save([...exp.goals, goal])) {
      name.value = '';
      value.value = '';
      revenue.checked = false;
    }
    add.disabled = false;
  });

  box.append(
    h('h2', {}, 'Goals'),
    h(
      'p',
      { class: 'muted' },
      'A goal is what counts as success: a click, a page reached, a purchase. Results are measured against it.',
    ),
    list,
    h(
      'div',
      { class: 'row' },
      field('Goal name', name),
      h('label', { class: 'field' }, h('span', { class: 'lbl' }, 'What counts'), kind),
      h('label', { class: 'field' }, valueLabel, value),
    ),
    h(
      'label',
      { class: 'check' },
      revenue,
      'This goal carries revenue (the value sent with it counts as money)',
    ),
    h('p', {}, add),
    msg,
  );
  return box;
}
